import {
  CHAT_DIALOG_SURFACE_CLASS_NAME,
  CHAT_DIALOG_HEADER_CLASS_NAME,
  CHAT_DIALOG_DESCRIPTION_CLASS_NAME,
  CHAT_DIALOG_FOOTER_CLASS_NAME,
} from "@/features/chat/chat-dialog-styles";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { endOfDay } from "date-fns";
import { queryClient } from "@/ajax/queryClient";
import { useTRPC, type RouterOutputs } from "@/trpc";
import { toast } from "sonner";
import {
  Loader2,
  FilePlus,
  ListChecks,
  Check,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { PdfFileIcon } from "@/components/icons/pdf-file-icon";
import { PdfFileDropzone } from "@/components/file-upload/pdf-file-dropzone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SingleDatePicker } from "@/components/schedule-input/single-date-picker";
import { cn } from "@/lib/utils";
import { PdfBuilderEmbed } from "@/features/org-forms/pdf/pdf-builder-embed";
import type { BuilderEmbed } from "@/features/org-forms/pdf/pdf-builder-embed";
import { PdfSignEmbed } from "@/features/hiring-process/pdf/pdf-sign-embed";
import { FileMappingStatus, FormScope } from "@api/shared-runtime/enums";

type Step =
  | { kind: "choose" }
  | { kind: "pick" }
  | { kind: "upload" }
  | { kind: "builder"; formId: string; embed: BuilderEmbed }
  | { kind: "no-builder"; formId: string }
  | { kind: "org-sign"; formId: string; assignmentId: string }
  | { kind: "publishing" }
  | { kind: "sending" };

type FinalizeSigningResult =
  RouterOutputs["organizationForm"]["finalizePdfSigning"];

interface SendFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationBusinessUnitId: string;
  recipientUserId: string;
  /** Resumes an orphaned draft's org-sign step instead of starting a fresh one. */
  resumeDraft?: {
    assignmentId: string;
    formId: string;
    orgSignerCompleted: boolean;
    formSubmissionId: string | null;
  };
  /** Called once the assignment is sent, with the (possibly newly created) chat thread id. */
  onSent: (chatThreadId: string) => void;
}

export function SendFormDialog({
  open,
  onOpenChange,
  organizationBusinessUnitId,
  recipientUserId,
  resumeDraft,
  onSent,
}: SendFormDialogProps) {
  const trpc = useTRPC();
  const [step, setStep] = useState<Step>(
    resumeDraft
      ? {
          kind: "org-sign",
          formId: resumeDraft.formId,
          assignmentId: resumeDraft.assignmentId,
        }
      : { kind: "choose" },
  );
  const [deadline, setDeadline] = useState<Date | undefined>(undefined);
  const [uploadName, setUploadName] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [orgSignError, setOrgSignError] = useState<string | null>(null);
  const [orgSignReady, setOrgSignReady] = useState(
    Boolean(resumeDraft?.orgSignerCompleted),
  );
  const [isCheckingResume, setIsCheckingResume] = useState(
    Boolean(
      resumeDraft &&
        !resumeDraft.orgSignerCompleted &&
        resumeDraft.formSubmissionId,
    ),
  );
  // Only a draft this dialog session created gets auto-canceled on close —
  // closing a resumed draft means "decide later"; only the banner's explicit
  // Discard action cancels those.
  const [draftCreatedThisSession, setDraftCreatedThisSession] = useState(false);
  const staleCheckRanRef = useRef(false);

  const { data: forms } = useQuery(
    trpc.organizationForm.listForms.queryOptions(
      { organizationBusinessUnitId },
      { enabled: open && step.kind === "pick" },
    ),
  );

  const createDraftMutation = useMutation(
    trpc.organizationForm.createPdfFormDraft.mutationOptions({
      onError: (err) => toast.error(err.message || "Failed to upload PDF"),
    }),
  );
  const syncFieldsMutation = useMutation(
    trpc.organizationForm.syncPdfFields.mutationOptions(),
  );
  const publishMutation = useMutation(
    trpc.organizationForm.publishPdfForm.mutationOptions(),
  );
  const sendMutation = useMutation(
    trpc.organizationForm.sendFormAssignment.mutationOptions(),
  );
  const createAssignmentDraftMutation = useMutation(
    trpc.organizationForm.createFormAssignmentDraft.mutationOptions(),
  );
  const finalizeSendMutation = useMutation(
    trpc.organizationForm.finalizeSendFormAssignment.mutationOptions(),
  );
  const cancelAssignmentMutation = useMutation(
    trpc.organizationForm.cancelFormAssignment.mutationOptions(),
  );
  const checkStaleSignatureMutation = useMutation(
    trpc.organizationForm.finalizePdfSigning.mutationOptions(),
  );
  const checkStaleSignatureMutateRef = useRef(
    checkStaleSignatureMutation.mutate,
  );
  checkStaleSignatureMutateRef.current = checkStaleSignatureMutation.mutate;

  useEffect(() => {
    if (staleCheckRanRef.current) {
      return;
    }
    staleCheckRanRef.current = true;
    if (
      !resumeDraft ||
      resumeDraft.orgSignerCompleted ||
      !resumeDraft.formSubmissionId
    ) {
      return;
    }
    // The DB signer row can lag DocuSeal if the tab died before the original
    // finalize call landed — sync from the vendor before deciding whether to
    // reopen a dead embed or jump straight to "Send now".
    checkStaleSignatureMutateRef.current(
      { formSubmissionId: resumeDraft.formSubmissionId },
      {
        onSuccess: (result) => {
          if (result.signerStatus.organization === "completed") {
            setOrgSignReady(true);
          }
          setIsCheckingResume(false);
        },
        onError: () => setIsCheckingResume(false),
      },
    );
  }, [resumeDraft]);

  const isBusy =
    createDraftMutation.isPending ||
    syncFieldsMutation.isPending ||
    publishMutation.isPending ||
    sendMutation.isPending ||
    createAssignmentDraftMutation.isPending ||
    finalizeSendMutation.isPending;

  const closeAfterSent = () => {
    onOpenChange(false);
    // The draft roster for this pair may have changed (created, sent, or an
    // abandoned draft whose best-effort cancel could silently fail) — refresh
    // the composer's gate so its banner reflects reality without waiting for a
    // refocus refetch.
    void queryClient.invalidateQueries(
      trpc.organizationForm.listFormAssignmentDrafts.queryOptions({
        organizationBusinessUnitId,
        recipientUserId,
      }),
    );
    // Reset after the close animation.
    setTimeout(() => {
      setStep({ kind: "choose" });
      setDeadline(undefined);
      setUploadName("");
      setUploadFile(null);
      setSelectedFormId(null);
      setOrgSignError(null);
    }, 300);
  };

  const close = () => {
    // Best-effort: a draft the org started signing but abandoned shouldn't
    // linger — the recipient never sees drafts anyway, so this is safe even
    // if it fails. Only applies to a draft created in this dialog session;
    // a resumed draft is left alone so the banner keeps blocking on it.
    if (step.kind === "org-sign" && draftCreatedThisSession) {
      cancelAssignmentMutation.mutate({
        organizationBusinessUnitId,
        assignmentId: step.assignmentId,
      });
    }
    closeAfterSent();
  };

  // Anchor the deadline to the end of the picked day in the sender's local
  // time, so a same-day deadline doesn't read as already passed.
  const deadlineAt = () => (deadline ? endOfDay(deadline) : undefined);

  const sendAssignment = async (formId: string) => {
    setStep({ kind: "sending" });
    try {
      const result = await sendMutation.mutateAsync({
        organizationBusinessUnitId,
        formId,
        recipientUserId,
        deadlineAt: deadlineAt(),
      });
      toast.success("Form sent");
      onSent(result.chatThreadId);
      closeAfterSent();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send form");
      setStep({ kind: "pick" });
    }
  };

  // PDFs requiring an org signature go through the draft flow: the org signs
  // its own part before the assignment is sent, so the provider never sees a
  // half-filled document.
  const startOrgSignDraft = async (formId: string) => {
    try {
      const draft = await createAssignmentDraftMutation.mutateAsync({
        organizationBusinessUnitId,
        formId,
        recipientUserId,
      });
      setOrgSignError(null);
      setDraftCreatedThisSession(true);
      setStep({ kind: "org-sign", formId, assignmentId: draft.assignmentId });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to prepare form",
      );
      setStep({ kind: "pick" });
    }
  };

  const finalizeSend = async (assignmentId: string) => {
    try {
      const result = await finalizeSendMutation.mutateAsync({
        organizationBusinessUnitId,
        assignmentId,
        deadlineAt: deadlineAt(),
      });
      toast.success("Form sent");
      onSent(result.chatThreadId);
      closeAfterSent();
    } catch (err) {
      // The signature is already recorded — let the org retry the (idempotent)
      // send instead of losing their signed progress.
      setOrgSignError(
        err instanceof Error ? err.message : "Failed to send form",
      );
    }
  };

  const handleOrgSigned = (
    assignmentId: string,
    result: FinalizeSigningResult,
  ) => {
    if (result.signerStatus.organization !== "completed") {
      toast.error(
        "This document isn't complete yet. Fill in all required fields and complete the form, then try again.",
      );
      return;
    }
    // The signature is now on record. If the send fails and the org closes the
    // dialog, don't discard that signed work — leave the draft for the banner
    // to resume ("Send now") instead of auto-canceling it.
    setDraftCreatedThisSession(false);
    void finalizeSend(assignmentId);
  };

  const handlePickSend = async (formId: string) => {
    const selected = (forms ?? []).find((f) => f.id === formId);
    if (selected?.requiresOrgSignature) {
      await startOrgSignDraft(formId);
    } else {
      await sendAssignment(formId);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) {
      return;
    }

    const pdfBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(uploadFile);
    });

    try {
      const result = await createDraftMutation.mutateAsync({
        organizationBusinessUnitId,
        name: uploadName.trim(),
        description: null,
        fileName: uploadFile.name,
        pdfBase64,
        scope: FormScope.Instance,
      });
      if (result.builderEmbed) {
        setStep({
          kind: "builder",
          formId: result.form.id,
          embed: result.builderEmbed,
        });
      } else {
        setStep({ kind: "no-builder", formId: result.form.id });
      }
    } catch {
      // Error already surfaced via the mutation's onError toast.
    }
  };

  const handlePublishAndSend = async (formId: string) => {
    setStep({ kind: "publishing" });
    let requiresOrgSignature = false;
    try {
      const synced = await syncFieldsMutation.mutateAsync({
        organizationBusinessUnitId,
        formId,
      });
      if ((synced?.fields ?? []).length === 0) {
        toast.error(
          "No fields found. Add at least one field to the PDF, then try again.",
        );
        setStep({ kind: "upload" });
        return;
      }
      const published = await publishMutation.mutateAsync({
        organizationBusinessUnitId,
        formId,
      });
      requiresOrgSignature = published.requiresOrgSignature;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to publish form",
      );
      setStep({ kind: "upload" });
      return;
    }
    if (requiresOrgSignature) {
      await startOrgSignDraft(formId);
    } else {
      await sendAssignment(formId);
    }
  };

  function renderContent() {
    switch (step.kind) {
      case "choose":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <button
              type="button"
              onClick={() => setStep({ kind: "pick" })}
              className="flex min-w-0 flex-col items-center gap-3 rounded-xl border border-slate-300 bg-white p-5 text-slate-900 shadow-xs transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <ListChecks size={22} className="text-primary" />
              <span className="text-sm font-medium">Pick a reusable form</span>
              <span className="text-xs text-slate-500 text-center">
                A form or PDF template from your library.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setStep({ kind: "upload" })}
              className="flex min-w-0 flex-col items-center gap-3 rounded-xl border border-slate-300 bg-white p-5 text-slate-900 shadow-xs transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <FilePlus size={22} className="text-primary" />
              <span className="text-sm font-medium">Upload a one-off PDF</span>
              <span className="text-xs text-slate-500 text-center">
                A document just for this provider.
              </span>
            </button>
          </div>
        );

      case "pick": {
        const nativeForms = (forms ?? []).filter(
          (f) => f.templateKind !== "file",
        );
        const pdfForms = (forms ?? []).filter((f) => f.templateKind === "file");

        const renderFormRow = (f: NonNullable<typeof forms>[number]) => {
          const isPdf = f.templateKind === "file";
          const notReady =
            isPdf && f.pdfMappingStatus !== FileMappingStatus.Ready;
          const isSelected = selectedFormId === f.id;
          return (
            <button
              key={f.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={isBusy || notReady}
              onClick={() => setSelectedFormId(f.id)}
              className={cn(
                "flex min-h-12 w-full min-w-0 items-center gap-2 px-3 py-3 text-left text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                isSelected
                  ? "bg-primary/5 text-primary"
                  : "hover:bg-slate-50 disabled:hover:bg-transparent",
              )}
            >
              {isPdf ? (
                <PdfFileIcon
                  size={14}
                  className={cn(
                    "shrink-0",
                    isSelected ? "text-primary" : "text-slate-500",
                  )}
                />
              ) : (
                <ListChecks
                  size={14}
                  className={cn(
                    "shrink-0",
                    isSelected ? "text-primary" : "text-slate-500",
                  )}
                />
              )}
              <span className="flex-1 truncate">{f.name}</span>
              {notReady && (
                <span className="text-xs text-slate-500 shrink-0">
                  Not ready
                </span>
              )}
              {isSelected && (
                <Check size={14} className="text-primary shrink-0" />
              )}
            </button>
          );
        };

        const sectionLabel = (label: string, hint: string) => (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-3 pb-2 pt-3">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              {label}
            </span>
            <span className="text-[11px] text-slate-500">{hint}</span>
          </div>
        );

        return (
          <div className="space-y-3">
            <div
              role="radiogroup"
              aria-label="Reusable forms"
              className="max-h-72 overflow-auto rounded-xl border border-slate-300 bg-white"
            >
              {(forms ?? []).length === 0 ? (
                <p className="text-sm text-slate-500 italic px-3 py-4">
                  No reusable forms yet. Create one in Forms, or upload a
                  one-off PDF instead.
                </p>
              ) : (
                <>
                  {pdfForms.length > 0 && (
                    <div className="py-1">
                      {sectionLabel("PDF documents", "filled and signed")}
                      {pdfForms.map(renderFormRow)}
                    </div>
                  )}
                  {nativeForms.length > 0 && (
                    <div
                      className={cn(
                        "py-1",
                        pdfForms.length > 0 && "border-t border-slate-200",
                      )}
                    >
                      {sectionLabel("Forms", "filled out in the app")}
                      {nativeForms.map(renderFormRow)}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Deadline (optional)</Label>
              <SingleDatePicker
                date={deadline}
                onDateChange={setDeadline}
                placeholder="No deadline"
              />
            </div>
            <DialogFooter className={CHAT_DIALOG_FOOTER_CLASS_NAME}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep({ kind: "choose" })}
              >
                Back
              </Button>
              <Button
                type="button"
                disabled={!selectedFormId || isBusy}
                onClick={() =>
                  selectedFormId && void handlePickSend(selectedFormId)
                }
              >
                {isBusy ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send form"
                )}
              </Button>
            </DialogFooter>
          </div>
        );
      }

      case "upload":
        return (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Document name</Label>
              <Input
                placeholder="e.g. Direct Deposit Form"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>PDF</Label>
              <PdfFileDropzone file={uploadFile} onFile={setUploadFile} />
            </div>
            <div className="space-y-1.5">
              <Label>Deadline (optional)</Label>
              <SingleDatePicker
                date={deadline}
                onDateChange={setDeadline}
                placeholder="No deadline"
              />
            </div>
            <DialogFooter className={CHAT_DIALOG_FOOTER_CLASS_NAME}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep({ kind: "choose" })}
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={() => void handleUpload()}
                disabled={isBusy || !uploadFile || !uploadName.trim()}
              >
                {createDraftMutation.isPending ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  "Continue to Field Mapping"
                )}
              </Button>
            </DialogFooter>
          </div>
        );

      case "builder":
        return (
          <div className="flex flex-col flex-1 min-h-0 gap-4">
            <p className="text-sm text-slate-600 shrink-0">
              Map your PDF fields below. When you&apos;re done, click{" "}
              <strong>Done — Send</strong>.
            </p>
            <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-slate-200">
              <PdfBuilderEmbed embed={step.embed} />
            </div>
            <DialogFooter
              className={`${CHAT_DIALOG_FOOTER_CLASS_NAME} shrink-0`}
            >
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handlePublishAndSend(step.formId)}
                disabled={isBusy}
              >
                {isBusy ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Done — Send"
                )}
              </Button>
            </DialogFooter>
          </div>
        );

      case "no-builder":
        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
              Map the fields in DocuSeal, then click <strong>Send</strong> to
              import them and deliver the form.
            </div>
            <DialogFooter className={CHAT_DIALOG_FOOTER_CLASS_NAME}>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handlePublishAndSend(step.formId)}
                disabled={isBusy}
              >
                {isBusy ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send"
                )}
              </Button>
            </DialogFooter>
          </div>
        );

      case "org-sign":
        return (
          <div className="flex flex-col flex-1 min-h-0 gap-4">
            <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-slate-200">
              {orgSignError ? (
                <div className="flex flex-col items-center justify-center h-full py-16 gap-3 px-6">
                  <AlertCircle className="h-8 w-8 text-red-400" />
                  <p className="text-sm text-red-600 text-center">
                    {orgSignError}
                  </p>
                  <Button
                    type="button"
                    onClick={() => void finalizeSend(step.assignmentId)}
                    disabled={isBusy}
                  >
                    {finalizeSendMutation.isPending ? (
                      <>
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                        Retrying...
                      </>
                    ) : (
                      "Retry send"
                    )}
                  </Button>
                </div>
              ) : orgSignReady ? (
                <div className="flex flex-col items-center justify-center h-full py-16 gap-3 px-6">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  <p className="text-sm text-slate-600 text-center">
                    You&apos;ve already signed this form.
                  </p>
                  <Button
                    type="button"
                    onClick={() => void finalizeSend(step.assignmentId)}
                    disabled={isBusy}
                  >
                    {finalizeSendMutation.isPending ? (
                      <>
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send now"
                    )}
                  </Button>
                </div>
              ) : isCheckingResume ? (
                <div className="flex flex-col items-center justify-center h-full py-16 gap-3">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                  <p className="text-sm text-slate-500">
                    Checking signature status...
                  </p>
                </div>
              ) : (
                <PdfSignEmbed
                  formId={step.formId}
                  anchor={{
                    kind: "assignment",
                    assignmentId: step.assignmentId,
                  }}
                  onSigned={(result) =>
                    handleOrgSigned(step.assignmentId, result)
                  }
                  onError={(message) => toast.error(message)}
                />
              )}
            </div>
            <DialogFooter
              className={`${CHAT_DIALOG_FOOTER_CLASS_NAME} shrink-0 sm:items-end sm:justify-between`}
            >
              <div className="space-y-1.5">
                <Label>Deadline (optional)</Label>
                <SingleDatePicker
                  date={deadline}
                  onDateChange={setDeadline}
                  placeholder="No deadline"
                />
              </div>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
            </DialogFooter>
          </div>
        );

      case "publishing":
      case "sending":
        return (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <p className="text-sm text-slate-500">
              {step.kind === "publishing"
                ? "Importing fields and publishing..."
                : "Sending..."}
            </p>
          </div>
        );
    }
  }

  function getTitle() {
    switch (step.kind) {
      case "choose":
        return "Send a form";
      case "pick":
        return "Pick a reusable form";
      case "upload":
        return "Upload a one-off PDF";
      case "builder":
      case "no-builder":
      case "publishing":
        return "Map PDF Fields";
      case "org-sign":
        return "Complete your part";
      case "sending":
        return "Send a form";
    }
  }

  function getDescription() {
    switch (step.kind) {
      case "choose":
        return "Send a form directly to this provider, outside any hiring process.";
      case "pick":
        return "Choose a PDF document to be signed or a form to be filled out. It's delivered right here in the chat.";
      case "upload":
        return "Upload the PDF this provider will fill out. Already filled it out offline? Just map the provider's fields in the next step.";
      case "builder":
        return "Use the visual editor to map each PDF field to a form field.";
      case "no-builder":
        return "Open DocuSeal to map fields, then sync them here.";
      case "org-sign":
        return "Fill in and sign your part — the provider receives the form once you're done.";
      case "publishing":
      case "sending":
        return null;
    }
  }

  const description = getDescription();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className={cn(
          CHAT_DIALOG_SURFACE_CLASS_NAME,
          "gap-5 p-5 sm:p-6",
          step.kind === "builder" || step.kind === "org-sign"
            ? "flex h-[90dvh] min-h-0 flex-col [&.dialog-content]:overflow-hidden"
            : "overflow-y-auto",
        )}
        // Inline style beats the global `.dialog-content { max-width: 1000px }`.
        style={
          step.kind === "builder" || step.kind === "org-sign"
            ? { width: "90vw", maxWidth: "none" }
            : undefined
        }
      >
        <DialogHeader
          className={`${CHAT_DIALOG_HEADER_CLASS_NAME} shrink-0 pr-8`}
        >
          <DialogTitle>{getTitle()}</DialogTitle>
          {description && (
            <DialogDescription className={CHAT_DIALOG_DESCRIPTION_CLASS_NAME}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
